import { Box, TextField } from '@mui/material';
import type { GridFilterInputValueProps, GridFilterOperator } from '@mui/x-data-grid-pro';

import { useDateBetweenFilterInput } from '../hooks/useDateBetweenFilterInput';
import { normalizeDateBetweenValue } from '../utils/dateBetweenFilterValue';

/**
 * CSS hook for the filter panel to widen the value slot when this two-field
 * input renders (see AnalyticalDatasetResultsTab's panel sx).
 */
export const DATE_BETWEEN_FILTER_INPUT_CLASS = 'ad-date-between-filter-input';

export interface DateBetweenFilterInputProps extends GridFilterInputValueProps {
  fromLabel: string;
  toLabel: string;
  invalidRangeHelperText: string;
}

/**
 * Two-date range input for the results grid's custom `between` filter
 * operator — presentational; state and validation live in
 * useDateBetweenFilterInput. Mirrors MUI's GridFilterInputDate conventions
 * (standard variant, native date input, shrunk label). An inverted range
 * renders both fields in their error state and never reaches the API.
 */
export const DateBetweenFilterInput = ({
  item,
  applyValue,
  focusElementRef,
  fromLabel,
  toLabel,
  invalidRangeHelperText,
}: DateBetweenFilterInputProps) => {
  const { start, end, hasRangeError, onStartChange, onEndChange } = useDateBetweenFilterInput({
    item,
    applyValue,
  });

  return (
    <Box
      className={DATE_BETWEEN_FILTER_INPUT_CLASS}
      sx={{ display: 'flex', gap: 2, width: '100%' }}
    >
      <TextField
        label={fromLabel}
        type="date"
        variant="standard"
        value={start}
        onChange={onStartChange}
        error={hasRangeError}
        inputRef={focusElementRef}
        sx={{ flex: 1 }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { max: '9999-12-31', 'data-testid': 'ad-date-between-start' },
        }}
      />
      <TextField
        label={toLabel}
        type="date"
        variant="standard"
        value={end}
        onChange={onEndChange}
        error={hasRangeError}
        helperText={hasRangeError ? invalidRangeHelperText : undefined}
        sx={{ flex: 1 }}
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { max: '9999-12-31', 'data-testid': 'ad-date-between-end' },
        }}
      />
    </Box>
  );
};

export interface DateBetweenFilterOperatorLabels {
  label: string;
  fromLabel: string;
  toLabel: string;
  invalidRangeHelperText: string;
}

/**
 * The custom `between` filter operator (inclusive both ends) offered on the
 * results grid's date columns alongside MUI's built-in date operators. The
 * grid filters server-side (mode="server"), so no client apply function.
 */
export const buildDateBetweenFilterOperator = ({
  label,
  fromLabel,
  toLabel,
  invalidRangeHelperText,
}: DateBetweenFilterOperatorLabels): GridFilterOperator => ({
  value: 'between',
  label,
  getApplyFilterFn: () => null,
  InputComponent: DateBetweenFilterInput,
  InputComponentProps: { fromLabel, toLabel, invalidRangeHelperText },
  getValueAsString: (value) => normalizeDateBetweenValue(value).filter(Boolean).join(' – '),
});
